import React, { useCallback, useMemo } from 'react';
import SettingsIcon from '@atlaskit/icon/core/settings';
import { fg } from '@atlassian/jira-feature-gating';
import {
	COMMAND_PALETTE_PERSONAL_SETTINGS_PRIORITY,
	COMMAND_PALETTE_SECTIONS,
} from '@atlassian/jira-command-palette-common/src/common/constants.tsx';
import { useCommandKeywords } from '@atlassian/jira-command-palette-common/src/controllers/use-command-keywords/index.tsx';
import { COMMAND_PALETTE_REGISTRY_IDS } from '@atlassian/jira-command-palette-registry/src/common/constants/registry/index.tsx';
import { useRegisterCommands } from '@atlassian/jira-command-palette-registry/src/controllers/use-register-commands/index.tsx';
import {
	type Command,
	CommandActionType,
} from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import {
	ThemeOptions,
	type Theme,
} from '@atlassian/jira-common-constants/src/theme-preference.tsx';
import { useCustomNavBarColorWarnings } from '@atlassian/jira-custom-nav-bar-colors-flag/src/controllers/index.tsx';
import useCustomNavBarColorsFlag from '@atlassian/jira-custom-nav-bar-colors-flag/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 } from '@atlassian/jira-intl/src/v2/types.tsx';
import { useThemePreference } from '@atlassian/jira-router-resources-theme-preference/src/controllers/resource-erai-1615-old/index.tsx';
import { messages } from './messages.tsx';

export const useThemeModeCommands = (): Command[] => {
	const { formatMessage } = useIntl();
	const { theme: currentTheme = 'light', setTheme } = useThemePreference();
	const { triggerCustomNavBarColorsFlag } = useCustomNavBarColorsFlag();

	const setThemeWithCustomNavBarFlag = useCallback(
		async (newTheme: Theme) => {
			await setTheme(newTheme);
			triggerCustomNavBarColorsFlag(newTheme);
		},
		[setTheme, triggerCustomNavBarColorsFlag],
	);

	const { getKeywords } = useCommandKeywords();

	const createThemeCommand = useCallback(
		(
			theme: Theme,
			message: MessageDescriptorV2,
			priority: COMMAND_PALETTE_PERSONAL_SETTINGS_PRIORITY,
		): Command => ({
			id: `command-palette-integration-change-theme-mode-${theme}`,
			name: formatMessage(message),
			keywords:
				theme === 'auto'
					? getKeywords('matchSystemThemeSynonyms')
					: getKeywords('switchDarkThemeSynonyms'),
			primaryAction: {
				type: CommandActionType.PERFORM,
				perform: () => setThemeWithCustomNavBarFlag(theme),
			},
			components: {
				LeftIcon: () => (
					<SettingsIcon
						spacing="spacious"
						label={fg('deliveroo_compal_a11y_fixes') ? '' : formatMessage(message)}
						aria-hidden={fg('deliveroo_compal_a11y_fixes') ? true : undefined}
					/>
				),
			},
			section: COMMAND_PALETTE_SECTIONS.personalSettings,
			priority,
			analytics: {
				action: 'toggleTheme',
			},
		}),
		[formatMessage, getKeywords, setThemeWithCustomNavBarFlag],
	);

	const themeModeCommands = useMemo(() => {
		const availableThemesMode = {
			dark: createThemeCommand(
				'dark',
				messages.commandDarkTheme,
				COMMAND_PALETTE_PERSONAL_SETTINGS_PRIORITY.switchToDarkTheme,
			),
			light: createThemeCommand(
				'light',
				messages.commandLightTheme,
				COMMAND_PALETTE_PERSONAL_SETTINGS_PRIORITY.switchToLightTheme,
			),
			auto: createThemeCommand(
				'auto',
				messages.commandAutoTheme,
				COMMAND_PALETTE_PERSONAL_SETTINGS_PRIORITY.switchToSystemTheme,
			),
		};

		const listOfCommands: Command[] = [];
		Object.entries(availableThemesMode).forEach(([key, command]) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			if (key !== currentTheme && ThemeOptions.includes(key as Theme)) listOfCommands.push(command);
		});

		return listOfCommands;
	}, [createThemeCommand, currentTheme]);

	return themeModeCommands;
};

export const ThemeModeCommands = () => {
	const [{ flagIsDismissed }] = useCustomNavBarColorWarnings();
	const command: Command[] = useThemeModeCommands();
	const commandIds = command.map(({ id }) => id).toString();
	useRegisterCommands(COMMAND_PALETTE_REGISTRY_IDS.THEME_MODE, command, [
		commandIds,
		flagIsDismissed,
	]);

	return null;
};
